import type { TelemetryEventKind, TelemetryEventRequest } from '@cradle7/shared';
import { VERSION } from './brand';

interface ReporterApi {
  reportError(req: TelemetryEventRequest): Promise<unknown>;
}

let api: ReporterApi | null = null;
let lastSentAt: Partial<Record<TelemetryEventKind, number>> = {};
const THROTTLE_MS = 5_000;

const APP_VERSION = VERSION.replace(/^v/, '');

/** Test hook: clear listeners + throttle state. */
export function _clearErrorReporter(): void {
  api = null;
  lastSentAt = {};
  if (typeof window !== 'undefined') {
    window.onerror = null;
  }
}

export function installErrorReporter(reporter: ReporterApi): void {
  api = reporter;
  lastSentAt = {};

  if (typeof window === 'undefined') return;

  window.onerror = (msg, src, _line, _col, err) => {
    void send('window_error', {
      message: typeof msg === 'string' ? msg : 'unknown',
      stack: err?.stack,
      url: src,
    });
    return false;
  };

  window.addEventListener('unhandledrejection', (ev) => {
    const reason = (ev as PromiseRejectionEvent).reason;
    const message = reason?.message ?? String(reason ?? 'unknown');
    void send('unhandled_rejection', {
      message,
      stack: reason?.stack,
    });
  });
}

export async function reportManualError(message: string, extra?: { stack?: string }): Promise<void> {
  await send('manual', { message, stack: extra?.stack });
}

export async function reportBoundaryError(message: string, stack?: string): Promise<void> {
  await send('react_error_boundary', { message, stack });
}

async function send(
  event: TelemetryEventKind,
  fields: { message: string; stack?: string; url?: string }
): Promise<void> {
  if (!api) return;
  const now = Date.now();
  const last = lastSentAt[event] ?? 0;
  if (now - last < THROTTLE_MS) return;
  lastSentAt[event] = now;

  const req: TelemetryEventRequest = {
    event,
    message: fields.message,
    stack: fields.stack,
    url: fields.url ?? (typeof window !== 'undefined' ? window.location.href : undefined),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    appVersion: APP_VERSION,
  };
  try {
    await api.reportError(req);
  } catch {
    /* errorReporter must NEVER throw — that would re-enter window.onerror */
  }
}
